import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, User as UserIcon } from "lucide-react";
import { Reveal } from "@/components/site/ui";
import { CtaBand } from "@/components/site/sections";
import { fetchBlogPost, fetchBlogPosts } from "@/lib/api";
import { IMAGES } from "@/data/content";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

// Lightweight, safe renderer: supports blank-line paragraphs, ## / ### headings,
// and "- " bullet lists. No HTML injection.
const renderContent = (raw) => {
  if (!raw) return null;
  const blocks = raw.replace(/\r\n/g, "\n").split(/\n\s*\n/);
  return blocks.map((block, idx) => {
    const lines = block.split("\n").map((l) => l.trimEnd());
    // Heading H2
    if (lines[0]?.startsWith("## ")) {
      return (
        <h2
          key={idx}
          className="font-head text-2xl sm:text-3xl font-medium tracking-tight text-[#1C1A17] mt-14 mb-4"
        >
          {lines[0].slice(3)}
        </h2>
      );
    }
    // Heading H3
    if (lines[0]?.startsWith("### ")) {
      return (
        <h3
          key={idx}
          className="font-head text-xl sm:text-2xl font-medium tracking-tight text-[#1C1A17] mt-10 mb-3"
        >
          {lines[0].slice(4)}
        </h3>
      );
    }
    // Bullet list
    if (lines.every((l) => l.startsWith("- ") || l === "")) {
      const items = lines.filter((l) => l.startsWith("- ")).map((l) => l.slice(2));
      return (
        <ul
          key={idx}
          className="list-disc pl-6 space-y-2 my-5 text-[#3F3B34] text-base leading-relaxed"
        >
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    }
    // Paragraph
    return (
      <p
        key={idx}
        className="text-[#3F3B34] text-base sm:text-lg leading-[1.85] my-5"
      >
        {block}
      </p>
    );
  });
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPost(null);
    (async () => {
      try {
        const [p, all] = await Promise.all([
          fetchBlogPost(slug),
          fetchBlogPosts().catch(() => []),
        ]);
        if (cancelled) return;
        setPost(p);
        setRelated(all.filter((x) => x.slug !== slug).slice(0, 3));
      } catch {
        if (!cancelled) setError("Post not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div
        className="pt-40 pb-24 text-center text-[#5C574F]"
        data-testid="blog-post-loading"
      >
        Loading…
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="pt-40 pb-24 mx-auto max-w-xl px-6 text-center">
        <h1 className="font-head text-3xl text-[#1C1A17]">Story not found</h1>
        <p className="mt-3 text-[#5C574F]">
          The article you&apos;re looking for is unavailable.
        </p>
        <Link
          to="/blog"
          data-testid="blog-post-back-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1C1A17] hover:text-[#B8935A]"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Journal
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Hero */}
      <section className="relative bg-[#1C1A17] pt-32 pb-16 lg:pb-24 overflow-hidden">
        {post.cover_image && (
          <>
            <img
              src={post.cover_image}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1C1A17] via-[#1C1A17]/70 to-[#1C1A17]/40" />
          </>
        )}
        <div className="relative z-10 mx-auto max-w-3xl px-6">
          <Link
            to="/blog"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Journal
          </Link>
          <Reveal>
            <div className="flex flex-wrap gap-2 mt-8">
              {post.tags?.map((t) => (
                <span
                  key={t}
                  className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#B8935A] border border-[#B8935A]/40 rounded-full px-3 py-1"
                >
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1
              data-testid="blog-post-title"
              className="font-head text-white text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter mt-5"
            >
              {post.title}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-white/70">
              <span className="inline-flex items-center gap-1.5">
                <UserIcon className="h-3.5 w-3.5" />
                {post.author}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" />
                {formatDate(post.created_at)}
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Cover image */}
      {post.cover_image && (
        <div className="mx-auto max-w-4xl px-6 -mt-8 lg:-mt-12">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full aspect-[16/9] object-cover rounded-2xl border border-black/10 shadow-xl shadow-black/10"
          />
        </div>
      )}

      {/* Body */}
      <article
        data-testid="blog-post-content"
        className="mx-auto max-w-3xl px-6 py-20 lg:py-28"
      >
        {post.excerpt && (
          <p className="font-head text-2xl sm:text-3xl leading-snug text-[#1C1A17] mb-10">
            {post.excerpt}
          </p>
        )}
        {renderContent(post.content)}
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[#F5F1EA] py-24">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <h2 className="font-head text-3xl sm:text-4xl font-medium tracking-tight text-[#1C1A17]">
              Keep reading
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/blog/${r.slug}`}
                  data-testid={`blog-related-${r.slug}`}
                  className="group flex flex-col"
                >
                  <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
                    <img
                      src={r.cover_image || IMAGES.interior1}
                      alt={r.title}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                    />
                  </div>
                  <h3 className="font-head text-xl font-medium tracking-tight text-[#1C1A17] mt-5 group-hover:text-[#B8935A] transition-colors">
                    {r.title}
                  </h3>
                  <p className="text-xs text-[#5C574F] mt-2">
                    {formatDate(r.created_at)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBand />
    </>
  );
}
